import React from "react";
import { withRouter } from "react-router-dom";
import { compose } from "react-apollo";
import jwt_decode from "jwt-decode";
import { withStore } from "../../store";
import { SVG, SVGImage } from "../../assets/images";
import { Button, InputGroup, PlainInput } from "@brightcove/studio-components";
import ExternalLink from "../ExternalLink/ExternalLink";
import { fetchHandler, getClientApiURL } from "../../utils";
import { consumeLoginTrigger } from "./loginTriggerCapture";
import "./Login.scss";
import _ from "lodash";
import { DESKTOP_MARGIN } from "../../utils/constants";
import { metUrl } from "../../config/metMainSite";

const FOCUSABLE_SELECTORS = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

class Login extends React.Component<any, any> {
  dialogRef = React.createRef<HTMLDivElement>();

  constructor(props) {
    super(props);
    this.state = {
      username: "",
      password: "",
      errors: {}
    };
  }

  componentDidMount() {
    const { history, location } = this.props;
    history.push(location.pathname);
    window.addEventListener('popstate', this.onPopState);
    document.addEventListener('keydown', this.onKeyDown);

    if (this.dialogRef.current) {
      const focusable = this.dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS);
      if (focusable.length > 0) {
        focusable[0].focus();
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener('popstate', this.onPopState);
    document.removeEventListener('keydown', this.onKeyDown);

    // MET2-2857/2870: return focus to the element that opened the modal.
    //
    // The login trigger is gated on `!displayLogIn` in Header.tsx, so it
    // gets unmounted as soon as the modal opens and re-mounted as soon as
    // it closes. The DOM update happens after componentWillUnmount fires,
    // so we defer the focus call until the next animation frame.
    //
    // See loginTriggerCapture.ts for the lookup strategy (uses the
    // captured reference if still attached, else re-queries by selector).
    requestAnimationFrame(() => {
      const returnTo = consumeLoginTrigger();
      if (returnTo && typeof returnTo.focus === "function") {
        returnTo.focus();
      }
    });
  }

  onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      this.props.hideLogIn();
      return;
    }

    if (e.key !== 'Tab') return;

    const dialog = this.dialogRef.current;
    if (!dialog) return;

    const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS));
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement as HTMLElement;

    if (e.shiftKey) {
      if (active === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (active === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  onPopState = () => {
    this.props.hideLogIn();
  }

  onChange = (field) => {
    return (value) => {
      this.setState({
        [field]: value
      });
    }
  }

  focusOnInput = (inputName) => {
    const input = document.querySelector(`input[name="${inputName}"]`) as HTMLElement;
    if (input) {
      input.focus();
      window.scrollTo({top: (input as HTMLInputElement).offsetTop - DESKTOP_MARGIN * 2, behavior: "smooth"});
    }
  }

  onSuccess = (jwt) => {
    const { setUser } = this.props;
    const response = jwt_decode(jwt);
    setUser(response);
  }

  onError = (error) => {
    const errors = {
      auth: true,
    };
    this.setState({
      errors: errors,
    });
  }

  onSubmit = (e) => {
    const { username, password } = this.state,
      loginUrl = getClientApiURL("/login"),
      options = {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ username, password })
      },
      errors = {};
    e.preventDefault();

    errors["username"] = _.isEmpty(username);
    errors["password"] = _.isEmpty(password);

    if(!errors["username"] && !errors["password"]) {
      fetchHandler(loginUrl, options, this.onSuccess, this.onError);
    }
    else {
      this.setState({ errors }, () => {
        if (errors["username"]) {
          this.focusOnInput("username");
        } else if (errors["password"]) {
          this.focusOnInput("password");
        }
      });
    }
  }

  render() {
    const { username, password, errors } = this.state;
    const { hideLogIn } = this.props;
    return (
      <div
        className="Login"
        role="dialog"
        aria-modal="true"
        aria-labelledby="login-modal-title"
        ref={this.dialogRef}
      >
        <button
          type="button"
          className="Login-close"
          onClick={hideLogIn}
          aria-label="Close login modal"
        >
          <img src={SVGImage.CloseThinIcon} alt="" aria-hidden="true" />
        </button>
        <div className="Login-body">
          <div className="Login-form">
            <form onSubmit={this.onSubmit}>
              <h1 className="Login-header" id="login-modal-title">Login</h1>
              <h3>Returning Customers</h3>
              {(errors.auth || errors.username || errors.password) &&
                <div className="error-message-container" role="alert" aria-live="assertive">
                  {errors.auth && <p className="error-message">Sorry! We were unable to log you in with the Username and Password provided. If this is your first time here, please register now. Otherwise, please try again.</p>}
                  {(errors.username || errors.password) && <p className="error-message">Please fix the following error(s) on the form. Selecting one of the following link(s) will move the page to the form error.</p>}
                  <ol>
                    {errors.username && <li className="error-step">Please correct errors with your <button type="button" className="error-link" onClick={() => this.focusOnInput("username")}>email or username</button></li>}
                    {errors.password && <li className="error-step">Please correct errors with your <button type="button" className="error-link" onClick={() => this.focusOnInput("password")}>password</button></li>}
                  </ol>
                </div>
              }
              <div className={`form-field ${errors.username ? "form-field-error" : ""}`}>
                <InputGroup
                  label="Email or Username"
                  contentRight={<span>Required</span>}
                  error={errors.username && <span className="form-field-error-message"><SVG.Alert className="form-field-error-alert" />Email or Username is a required field</span>}
                >
                  <PlainInput
                    name="username"
                    placeholder="janedoe@example.com or janedoe"
                    value={username}
                    onChange={this.onChange("username")}
                    aria-describedby={errors.username ? "username-error" : undefined}
                  />
                </InputGroup>
              </div>
              <div className={`form-field ${errors.password ? "form-field-error" : ""}`}>
                <InputGroup
                  label="Password"
                  contentRight={<span>Required</span>}
                  error={errors.password && <span className="form-field-error-message"><SVG.Alert className="form-field-error-alert" />Password is a required field</span>}
                >
                  <PlainInput
                    name="password"
                    type="password"
                    placeholder="Type your password"
                    value={password}
                    onChange={this.onChange("password")}
                    aria-describedby={errors.password ? "password-error" : undefined}
                  />
                </InputGroup>
              </div>
              <div className="form-field">
                <Button type='submit' theme="classic" className="btn btn-red">Sign In</Button>
              </div>
              <div className="form-field forgot-password">
                <ExternalLink url={metUrl("/account/forgot-password/")} text="Forgot Username or Password?" />
              </div>
              <div className="Login-disclaimer">
                <p>By signing in you agree to our <ExternalLink url={metUrl("/user-information/terms-and-conditions/")} text="Terms of Service" /> and acknowledge our <ExternalLink url={metUrl("/user-information/privacy-policy/")} text="Privacy Policy" />.</p>
              </div>
            </form>
          </div>
          <div className="Login-register">
            <h3>New Customers</h3>
            <p>Create an account to receive special ticket offers, information about events, or sign up to receive email communications.</p>
            <Button theme="classic" Component="a" componentProps={{href: metUrl("/account/register/"), target: "_blank"}} text="Register" className="btn btn-red" />
          </div>
        </div>
      </div>
    )
  }

};

export default compose(withRouter, withStore)(Login);
